import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IonCard, IonCardHeader, IonCardTitle, IonList, IonItem, IonLabel, IonButton, IonIcon, IonSpinner,
  IonInput, IonSelect, IonSelectOption, IonToggle, IonTextarea, IonRadioGroup, IonRadio, IonRippleEffect,
} from "@ionic/react";
import { addOutline, trashOutline, arrowUpOutline, arrowDownOutline, sendOutline, createOutline, closeOutline } from "ionicons/icons";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import EditSheet from "@/components/EditSheet";
import { IonDateInput } from "@/components/DateInput";
import SignDocCanvas from "@/components/SignDocCanvas";
import SendSignDocSheet from "@/components/SendSignDocSheet";
import SenderSignaturePicker from "@/components/SenderSignaturePicker";
import { useSavedAssets } from "@/components/SavedAssets";
import { FLOAT } from "@/components/SettingsRows";
import { pdfToPageImages } from "@/utils/pdfPageImages";
import { userHeaders } from "@/utils/userAuth";
import { confirmAlert } from "@/utils/confirmAlert";
import { toast } from "@/utils/toast";
import {
  FIELD_TYPES, SENDER_TYPES, senderUnsigned, fieldMeta, newField, newId, roleColor, roleLabel, b64ToBytes, DATE_FORMATS, formatDate,
} from "@/utils/signDocFields";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const MAX_PAGES = 40;

// /app/signatures/documents/:id — place signature fields on an uploaded PDF.
// The palette's field types drop (or tap, then tap the page) onto any page;
// each field belongs to a signer (their colour) or to the sender, filled in
// now. Signers are listed in signing order. Changes save themselves.
export default function AppSignDocEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);       // the saved document
  const [pages, setPages] = useState(null);   // page images
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [roles, setRoles] = useState([]);
  const [fields, setFields] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [armed, setArmed] = useState(null);   // a field type waiting for a tap on the page
  const [activeRole, setActiveRole] = useState(null);
  const [saveState, setSaveState] = useState("saved"); // saved | dirty | saving | error
  const [fieldSheet, setFieldSheet] = useState(null);  // a field being edited (a copy)
  const [roleSheet, setRoleSheet] = useState(null);    // { id, label }
  const [sendOpen, setSendOpen] = useState(false);
  const loaded = useRef(false);
  // the sender's own signature boxes: the default Branding signature goes in as one is placed
  const { assets: savedSignatures } = useSavedAssets("signature");
  const defaultSignature = savedSignatures.find((a) => a.isDefault) || null;

  // ── load the document and draw its pages ──
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const [dRes, fRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/user/sign-documents/${encodeURIComponent(id)}`, { headers: userHeaders() }),
          fetch(`${BACKEND_URL}/api/user/sign-documents/${encodeURIComponent(id)}/file`, { headers: userHeaders() }),
        ]);
        const d = await dRes.json().catch(() => ({}));
        const f = await fRes.json().catch(() => ({}));
        if (!dRes.ok || !d.document) throw new Error(d.detail || "This document could not be opened");
        if (!fRes.ok || !f.fileData) throw new Error(f.detail || "The PDF could not be loaded");
        if (!on) return;
        setDoc(d.document);
        setName(d.document.name || "");
        setRoles(d.document.roles || []);
        setFields(d.document.fields || []);
        setActiveRole((d.document.roles || [])[0]?.id || null);
        const imgs = await pdfToPageImages(b64ToBytes(f.fileData), { scale: 1.5, maxPages: MAX_PAGES });
        if (!on) return;
        setPages(imgs);
        setPageCount(imgs.length);
        loaded.current = true;
      } catch (err) { if (on) setError(err.message); }
    })();
    return () => { on = false; };
  }, [id]);

  // ── save, debounced: every change lands on its own ──
  const save = useCallback(async (payload) => {
    setSaveState("saving");
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/sign-documents/${encodeURIComponent(id)}`, {
        method: "PUT", headers: userHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save");
      setDoc(d.document);
      setSaveState("saved");
      return d.document;
    } catch (err) { setSaveState("error"); toast.error(err.message); return null; }
  }, [id]);
  const latest = useRef({});
  latest.current = { name, roles, fields, pageCount };
  useEffect(() => {
    if (!loaded.current) return undefined;
    setSaveState("dirty");
    const t = setTimeout(() => save(latest.current), 900);
    return () => clearTimeout(t);
  }, [name, roles, fields, save]);

  // ── fields ──
  const roleFor = (type) => (activeRole === "sender" && !SENDER_TYPES.includes(type) ? roles[0]?.id : activeRole) || roles[0]?.id;
  const place = (type, page, x, y) => {
    const f = newField(type, roleFor(type), page, x, y);
    // the sender's own signature or initials: the default saved signature
    // fills a signature box at once; otherwise the sheet asks for the image
    const ownMark = f.role === "sender" && (type === "signature" || type === "initials");
    if (ownMark && type === "signature" && defaultSignature) f.value = defaultSignature.data;
    setFields((list) => [...list, f]);
    setSelectedId(f.id);
    setArmed(null);
    if (ownMark && !f.value) setFieldSheet({ ...f });
  };
  const removeField = (fid) => { setFields((list) => list.filter((f) => f.id !== fid)); setSelectedId(null); };
  const saveFieldSheet = () => {
    const f = fieldSheet;
    const opts = String(f.optionsText ?? (f.options || []).join("\n")).split("\n").map((o) => o.trim()).filter(Boolean);
    const next = { ...f };
    delete next.optionsText;
    if (f.type === "dropdown" || f.type === "radio") next.options = opts.length ? opts.slice(0, 20) : ["Option 1"];
    if (next.role !== "sender") delete next.value;
    setFields((list) => list.map((x) => (x.id === f.id ? next : x)));
    setFieldSheet(null);
  };

  // Delete removes the selected field (when not typing in an input)
  useEffect(() => {
    const onKey = (e) => {
      if (!selectedId || fieldSheet || roleSheet) return;
      const tag = (e.target && e.target.tagName) || "";
      if (/INPUT|TEXTAREA|SELECT/.test(tag) || e.target?.isContentEditable) return;
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); removeField(selectedId); }
      if (e.key === "Escape") { setSelectedId(null); setArmed(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, fieldSheet, roleSheet]); // eslint-disable-line

  // ── signers (roles), in signing order ──
  const addRole = () => {
    if (roles.length >= 10) { toast.error("Up to 10 signers"); return; }
    const r = { id: newId(), label: `Signer ${roles.length + 1}` };
    setRoles((list) => [...list, r]);
    setActiveRole(r.id);
  };
  const moveRole = (i, d) => setRoles((list) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return list;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const removeRole = async (r) => {
    if (roles.length <= 1) { toast.error("A document needs at least one signer"); return; }
    const n = fields.filter((f) => f.role === r.id).length;
    if (!(await confirmAlert({
      header: "Remove this signer?",
      message: n ? `${r.label} and their ${n} field${n === 1 ? "" : "s"} are removed from this document.` : `${r.label} is removed from this document.`,
      confirmText: "Remove",
    }))) return;
    setFields((list) => list.filter((f) => f.role !== r.id));
    setRoles((list) => list.filter((x) => x.id !== r.id));
    if (activeRole === r.id) setActiveRole(roles.find((x) => x.id !== r.id)?.id || null);
  };

  const openSend = async () => {
    // the latest fields go out: save first if anything is pending
    const saved = saveState === "saved" ? doc : await save(latest.current);
    if (!saved) return;
    if (!(saved.fields || []).some((f) => f.role !== "sender")) { toast.error("Place at least one field for a signer first"); return; }
    // the sender's own signature boxes go out signed: an empty one opens for its image
    const unsigned = senderUnsigned(saved.fields);
    if (unsigned.length) { toast.error(unsigned[0].type === "initials" ? "Add your initials first" : "Add your signature first"); setSelectedId(unsigned[0].id); setFieldSheet({ ...unsigned[0] }); return; }
    setSendOpen(true);
  };

  const fieldsFor = (rid) => fields.filter((f) => f.role === rid).length;

  return (
    <AppLayout>
      <AppSeo title="Edit document" description="Place signature fields on your document." path="/app/signatures" />
      {error ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--ion-color-medium)" }}>{error}</div>
      ) : !doc || !pages ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 60, gap: 10, color: "var(--ion-color-medium)" }}>
          <IonSpinner name="crescent" /><span style={{ fontSize: "0.85rem" }}>Opening the document…</span>
        </div>
      ) : (
        <div className="sdoc-editor admin-profile admin-moderators">
          {/* ── the left rail: fields, then signers ── */}
          <aside className="sdoc-rail">
            {/* the document: its name, whether it is saved, and Send */}
            <IonCard className="mods-card sdoc-card sdoc-name-card">
              <IonCardHeader>
                <IonCardTitle className="type-title">
                  <span>Document</span>
                  <span className={`admin-badge ${saveState === "error" ? "admin-badge-red" : saveState === "saved" ? "admin-badge-green" : "admin-badge-slate"}`}>
                    {{ saved: "Saved", dirty: "Unsaved", saving: "Saving…", error: "Not saved" }[saveState]}
                  </span>
                </IonCardTitle>
              </IonCardHeader>
              <div className="sdoc-name-body">
                <IonInput {...FLOAT} label="Document name" value={name} onIonInput={(e) => setName(e.detail.value ?? "")} />
                <IonButton expand="block" color="success" onClick={openSend} style={{ "--color": "#ffffff" }}>
                  <IonIcon slot="start" icon={sendOutline} /> Send
                </IonButton>
              </div>
            </IonCard>
            <IonCard className="mods-card sdoc-card">
              <IonCardHeader><IonCardTitle className="type-title"><span>Fields</span></IonCardTitle></IonCardHeader>
              <p className="sdoc-hint">Drag a field onto the page, or tap it and then tap where it goes. It belongs to the signer selected below.</p>
              <div className="sdoc-palette">
                {FIELD_TYPES.map((t) => {
                  const off = activeRole === "sender" && !SENDER_TYPES.includes(t.type);
                  return (
                    <button key={t.type} type="button" draggable={!off} disabled={off}
                      className={`sdoc-chip ion-activatable${armed === t.type ? " is-armed" : ""}`}
                      style={{ "--chip-color": roleColor(roles, activeRole) }}
                      onDragStart={(e) => { e.dataTransfer.setData("text/sign-field", t.type); e.dataTransfer.effectAllowed = "copy"; }}
                      onClick={() => setArmed((a) => (a === t.type ? null : t.type))}>
                      <IonIcon icon={t.icon} /> {t.label}
                      <IonRippleEffect /> {/* the Ionic ripple on tap (the chip is already positioned and clipped) */}
                    </button>
                  );
                })}
              </div>
              {armed && (
                <div className="sdoc-armed">
                  Tap the page to place a {fieldMeta(armed).label.toLowerCase()} field.
                  <IonButton fill="clear" size="small" onClick={() => setArmed(null)} aria-label="Cancel"><IonIcon slot="icon-only" icon={closeOutline} /></IonButton>
                </div>
              )}
            </IonCard>

            <IonCard className="mods-card sdoc-card">
              <IonCardHeader>
                <IonCardTitle className="type-title">
                  <span>Signers</span>
                  <IonButton size="small" fill="outline" onClick={addRole}
                    style={{ "--color": "var(--ion-text-color, #0d1b2a)", "--border-color": "var(--ion-text-color, #0d1b2a)" }}>
                    <IonIcon slot="start" icon={addOutline} /> Add
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <p className="sdoc-hint">They sign in this order. Pick one to place their fields.</p>
              <IonRadioGroup value={activeRole} onIonChange={(e) => setActiveRole(e.detail.value)}>
                <IonList lines="full" style={{ padding: 0 }}>
                  {roles.map((r, i) => (
                    <IonItem key={r.id} className="sdoc-role">
                      <IonRadio slot="start" value={r.id} aria-label={`Place fields for ${r.label}`} style={{ "--color-checked": roleColor(roles, r.id) }} />
                      <span className="sdoc-dot" style={{ background: roleColor(roles, r.id) }}>{i + 1}</span>
                      <IonLabel>
                        {r.label}
                        <p>{fieldsFor(r.id)} field{fieldsFor(r.id) === 1 ? "" : "s"}</p>
                      </IonLabel>
                      <div slot="end" className="sdoc-role-actions">
                        <IonButton fill="clear" size="small" disabled={i === 0} onClick={() => moveRole(i, -1)} aria-label="Sign earlier" title="Sign earlier"><IonIcon slot="icon-only" icon={arrowUpOutline} /></IonButton>
                        <IonButton fill="clear" size="small" disabled={i === roles.length - 1} onClick={() => moveRole(i, 1)} aria-label="Sign later" title="Sign later"><IonIcon slot="icon-only" icon={arrowDownOutline} /></IonButton>
                        <IonButton fill="clear" size="small" onClick={() => setRoleSheet({ ...r })} aria-label="Rename" title="Rename"><IonIcon slot="icon-only" icon={createOutline} /></IonButton>
                        <IonButton fill="clear" color="danger" size="small" onClick={() => removeRole(r)} aria-label="Remove" title="Remove"><IonIcon slot="icon-only" icon={trashOutline} /></IonButton>
                      </div>
                    </IonItem>
                  ))}
                  <IonItem lines="none" className="sdoc-role">
                    <IonRadio slot="start" value="sender" aria-label="Place fields you fill in yourself" style={{ "--color-checked": roleColor(roles, "sender") }} />
                    <span className="sdoc-dot" style={{ background: roleColor(roles, "sender") }}>Y</span>
                    <IonLabel>
                      You (prefilled)
                      <p>Your signature, initials, text, dates and checkboxes, filled in now · {fieldsFor("sender")}</p>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonRadioGroup>
            </IonCard>
          </aside>

          {/* ── the document ── */}
          <main className="sdoc-main">
            <SignDocCanvas
              pages={pages} fields={fields} roles={roles} mode="edit"
              selectedId={selectedId} onSelect={setSelectedId}
              onChange={setFields}
              onEditField={(f) => setFieldSheet({ ...f })}
              onDeleteField={removeField}
              placing={!!armed} onPlace={(page, x, y) => armed && place(armed, page, x, y)}
              onDropType={(type, page, x, y) => place(type, page, x, y)}
            />
          </main>
        </div>
      )}

      {fieldSheet && (
        <EditSheet title={`${fieldMeta(fieldSheet.type).label} field`} onClose={() => setFieldSheet(null)} onSave={saveFieldSheet}>
          <IonSelect {...FLOAT} label="Filled in by" interface="popover" value={fieldSheet.role}
            onIonChange={(e) => setFieldSheet((f) => ({ ...f, role: e.detail.value }))}>
            {roles.map((r) => <IonSelectOption key={r.id} value={r.id}>{r.label}</IonSelectOption>)}
            {SENDER_TYPES.includes(fieldSheet.type) && <IonSelectOption value="sender">You (prefilled)</IonSelectOption>}
          </IonSelect>
          <IonInput {...FLOAT} label="Label" placeholder={fieldMeta(fieldSheet.type).label} value={fieldSheet.label}
            onIonInput={(e) => setFieldSheet((f) => ({ ...f, label: e.detail.value ?? "" }))} />
          {(fieldSheet.type === "date" || fieldSheet.type === "pickdate") && (
            <IonSelect {...FLOAT} label="Date format" interface="popover" value={fieldSheet.format || "long"}
              onIonChange={(e) => setFieldSheet((f) => ({ ...f, format: e.detail.value }))}>
              {DATE_FORMATS.map((d) => <IonSelectOption key={d.key} value={d.key}>{formatDate(new Date(2026, 8, 24), d.key)}</IonSelectOption>)}
            </IonSelect>
          )}
          {(fieldSheet.type === "dropdown" || fieldSheet.type === "radio") && (
            <IonTextarea {...FLOAT} label="Options (one per line)" autoGrow rows={3}
              value={fieldSheet.optionsText ?? (fieldSheet.options || []).join("\n")}
              onIonInput={(e) => setFieldSheet((f) => ({ ...f, optionsText: e.detail.value ?? "" }))} />
          )}
          {fieldSheet.role === "sender" ? (
            fieldSheet.type === "signature" || fieldSheet.type === "initials" ? (
              /* the sender's own mark: a saved Branding signature, a drawing or an upload */
              <SenderSignaturePicker type={fieldSheet.type} value={fieldSheet.value || null}
                onChange={(data) => setFieldSheet((f) => ({ ...f, value: data || "" }))} />
            ) : fieldSheet.type === "checkbox" ? (
              <IonItem lines="none" style={{ "--padding-start": "0" }}>
                <IonLabel>Checked</IonLabel>
                <IonToggle slot="end" checked={!!fieldSheet.value} onIonChange={(e) => setFieldSheet((f) => ({ ...f, value: e.detail.checked }))} />
              </IonItem>
            ) : fieldSheet.type === "pickdate" ? (
              <IonDateInput label="Date" value={fieldSheet.value || ""} onChange={(v) => setFieldSheet((f) => ({ ...f, value: v }))} />
            ) : (
              <IonInput {...FLOAT} label="Value" inputmode={fieldSheet.type === "number" ? "decimal" : "text"} value={fieldSheet.value || ""}
                onIonInput={(e) => setFieldSheet((f) => ({ ...f, value: e.detail.value ?? "" }))} />
            )
          ) : fieldSheet.type !== "date" && (
            <IonItem lines="none" style={{ "--padding-start": "0" }}>
              <IonLabel>Required</IonLabel>
              <IonToggle slot="end" checked={!!fieldSheet.required} onIonChange={(e) => setFieldSheet((f) => ({ ...f, required: e.detail.checked }))} />
            </IonItem>
          )}
          {fieldSheet.type === "date" && fieldSheet.role !== "sender" && (
            <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--ion-color-medium)" }}>Fills itself with the day {roleLabel(roles, fieldSheet.role)} signs.</p>
          )}
          <IonButton color="danger" onClick={() => { removeField(fieldSheet.id); setFieldSheet(null); }}>
            <IonIcon slot="start" icon={trashOutline} /> Delete field
          </IonButton>
        </EditSheet>
      )}

      {roleSheet && (
        <EditSheet title="Rename signer" onClose={() => setRoleSheet(null)} onSave={() => {
          const label = String(roleSheet.label || "").trim().slice(0, 40);
          if (!label) { toast.error("Enter a name"); return; }
          setRoles((list) => list.map((r) => (r.id === roleSheet.id ? { ...r, label } : r)));
          setRoleSheet(null);
        }}>
          <IonInput {...FLOAT} label="Signer name" placeholder="e.g. Tenant, Landlord, Witness" value={roleSheet.label}
            onIonInput={(e) => setRoleSheet((r) => ({ ...r, label: e.detail.value ?? "" }))} />
        </EditSheet>
      )}

      {sendOpen && doc && (
        <SendSignDocSheet doc={doc} onClose={() => setSendOpen(false)} onSent={() => { setSendOpen(false); navigate("/app/signatures", { replace: false }); }} />
      )}
    </AppLayout>
  );
}
